import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Activity, Mail, Check, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(!!token);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmailToken();
    }
  }, [token]);

  const verifyEmailToken = async () => {
    setVerifying(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (response.ok) {
        setVerified(true);
        setEmail(data.email || '');
        if (data.already_verified) {
          toast.info('Email was already verified');
        } else {
          toast.success('Email verified successfully!');
        }
      } else {
        setError(data.detail || 'Failed to verify email');
        toast.error(data.detail || 'Verification failed');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      toast.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResendLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setResendSent(true);
        if (data.already_verified) {
          toast.info('This email is already verified. You can sign in.');
        } else {
          toast.success('Verification email sent!');
        }
      } else {
        toast.error(data.detail || 'Failed to send verification email');
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Activity className="w-7 h-7 text-white" />
          </div>
          <span className="font-display text-2xl font-bold tracking-tight uppercase">
            SportsAnalysisAB
          </span>
        </Link>

        {/* Card */}
        <div className="bg-card border border-border rounded-sm p-8">
          {verifying ? (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
              <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-2">
                Verifying Email...
              </h2>
              <p className="text-muted-foreground">
                Please wait while we verify your email address.
              </p>
            </div>
          ) : verified ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-2">
                Email Verified!
              </h2>
              <p className="text-muted-foreground mb-6">
                {email ? (
                  <>Your email <span className="text-foreground font-medium">{email}</span> has been verified.</>
                ) : (
                  'Your email has been successfully verified.'
                )}
              </p>
              <p className="text-muted-foreground mb-6">
                You now have full access to all features.
              </p>
              <Button 
                className="btn-blaze rounded-sm font-bold uppercase tracking-wider"
                onClick={() => navigate('/login')}
                data-testid="goto-login-btn"
              >
                Sign In
              </Button>
            </div>
          ) : error ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-2">
                Verification Failed
              </h2>
              <p className="text-muted-foreground mb-6">
                {error}
              </p>
              
              {/* Resend verification form */}
              <div className="border-t border-border pt-6 mt-6">
                <p className="text-sm text-muted-foreground mb-4">
                  Need a new verification link?
                </p>
                {!resendSent ? (
                  <form onSubmit={handleResendVerification} className="space-y-3">
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="rounded-sm"
                      required
                      data-testid="resend-email-input"
                    />
                    <Button 
                      type="submit"
                      variant="outline"
                      className="w-full rounded-sm"
                      disabled={resendLoading}
                      data-testid="resend-verification-btn"
                    >
                      {resendLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Resend Verification
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-sm p-3">
                    <p className="text-sm text-green-400">
                      Verification email sent! Check your inbox.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* No token - show resend form */
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h1 className="font-display text-2xl font-bold uppercase tracking-tight mb-2">
                  Verify Your Email
                </h1>
                <p className="text-sm text-muted-foreground">
                  Enter your email to receive a verification link
                </p>
              </div>

              {!resendSent ? (
                <form onSubmit={handleResendVerification} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        className="pl-10 rounded-sm"
                        required
                        data-testid="verify-email-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-blaze rounded-sm font-bold uppercase tracking-wider"
                    disabled={resendLoading}
                    data-testid="send-verification-btn"
                  >
                    {resendLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Verification Link'
                    )}
                  </Button>
                </form>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-2">
                    Check Your Email
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    If an unverified account exists with that email, we've sent a verification link.
                  </p>
                  <Button 
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => setResendSent(false)}
                  >
                    Try Another Email
                  </Button>
                </div>
              )}
            </>
          )}

          <p className="text-sm text-muted-foreground text-center mt-6">
            Already verified?{' '}
            <Link to="/login" className="text-foreground hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
